import { useState, useEffect, useLayoutEffect, useRef, useCallback } from "react";
import { Popper, Paper, Box, ClickAwayListener } from "@mzc-pdc/ui";
import theme from "@styles/theme";

const SCREEN_MARGIN = 16; // Popper가 열릴 때 브라우저 상하단과 닿지 않기 위한 여백

const AdaptivePopper = ({
    open,
    anchorEl,
    placement,
    disablePortal = false,
    minHeight,
    maxHeight,
    children,
    wrapperSx = {},
    onClickAway = () => {},
    offset,
    ...popperProps
}) => {
    const popperRef = useRef(null);
    const contentWrapperRef = useRef(null);
    const [popperHeight, setPopperHeight] = useState(minHeight);
    const [dynamicPlacement, setDynamicPlacement] = useState(placement);

    // Popper의 최적 높이를 계산하는 함수
    const calculateHeight = useCallback(() => {
        // minHeight와 maxHeight가 없으면 높이 계산하지 않음
        if (minHeight === undefined && maxHeight === undefined) return;

        if (!anchorEl || !contentWrapperRef.current) return;

        const anchorRect = anchorEl.getBoundingClientRect();

        let availableHeight;
        if (dynamicPlacement?.startsWith("top")) {
            // Popper가 열리는 방향에 따른 사용 가능한 높이 계산
            availableHeight = anchorRect.top - SCREEN_MARGIN;
        } else {
            availableHeight = window.innerHeight - anchorRect.bottom - SCREEN_MARGIN;
        }

        const effectiveMinHeight = minHeight ?? 0;
        const effectiveMaxHeight = maxHeight ?? 10000;

        // 5. Popper의 최종 높이 계산
        let calculatedHeight = Math.max(effectiveMinHeight, Math.min(effectiveMaxHeight, availableHeight));
        setPopperHeight(calculatedHeight);
    }, [anchorEl, contentWrapperRef, dynamicPlacement, minHeight, maxHeight]);

    // Popper의 최적 배치 위치를 계산하는 함수
    const calculatePlacement = useCallback(() => {
        if (placement) {
            // 명시적인 placement가 있으면 그대로 사용
            setDynamicPlacement(placement);
            return;
        }

        if (!anchorEl || !open) return;

        // placement가 없으면 anchor 위치에 따라 자동으로 결정
        const anchorRect = anchorEl.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        const anchorCenterY = anchorRect.top + anchorRect.height / 2;

        // anchor가 화면 상단 절반에 있으면 아래쪽에, 하단 절반에 있으면 위쪽에 표시
        if (anchorCenterY < windowHeight / 2) {
            setDynamicPlacement("bottom-end");
        } else {
            setDynamicPlacement("top-end");
        }
    }, [placement, anchorEl, open]);

    // 초기 렌더링 시 placement와 높이를 동기적으로 계산 (깜빡임 방지)
    useLayoutEffect(() => {
        if (open) {
            calculatePlacement();
            calculateHeight();
        }
    }, [open, anchorEl, placement, calculateHeight, calculatePlacement]);

    // 뷰포트 크기 변경 감지 및 높이 조정
    useEffect(() => {
        if (open) {
            calculateHeight();
            calculatePlacement();
            window.addEventListener("resize", calculateHeight);

            let observer;
            if (popperRef.current) {
                observer = new MutationObserver(() => {
                    calculateHeight();
                });
                observer.observe(popperRef.current, {
                    attributes: true,
                    attributeFilter: ["data-popper-placement", "style"],
                });
            }

            return () => {
                window.removeEventListener("resize", calculateHeight);
                if (observer) observer.disconnect();
            };
        }
    }, [open, dynamicPlacement]);

    // Popper가 닫혀있으면 렌더링하지 않음
    if (open !== undefined && !open) return null;

    return (
        <Popper
            ref={popperRef}
            anchorEl={anchorEl}
            placement={dynamicPlacement}
            open={open}
            disablePortal={disablePortal}
            modifiers={[
                {
                    name: "flip",
                    enabled: false, // 자동 위치 반전 비활성화 (수동으로 placement 제어)
                },
                ...(offset
                    ? [
                          {
                              name: "offset",
                              options: { offset },
                          },
                      ]
                    : []),
            ]}
            sx={{
                borderRadius: "4px",
                border: `solid 1px ${theme.palette.greySecondary[100]}`,
                boxShadow: "0px 2px 4px 0px rgba(0, 0, 0, 0.16)",
                zIndex: 900,
            }}
            {...popperProps}
        >
            <ClickAwayListener onClickAway={onClickAway}>
                <Paper
                    ref={contentWrapperRef}
                    sx={{
                        // maxHeight가 없으면 높이를 자동으로 펼침
                        ...(popperHeight !== null
                            ? {
                                  height: `${popperHeight}px`, // 동적으로 계산된 높이
                                  minHeight: `${minHeight}px`,
                                  maxHeight: `${maxHeight}px`,
                              }
                            : {
                                  minHeight: `${minHeight}px`,
                                  maxHeight: `${maxHeight}px`,
                              }),
                        overflowX: "hidden",
                        overflowY: "hidden",
                        display: "flex",
                        flexDirection: "column",
                        "& .MuiTreeItem-content": {
                            borderTopRightRadius: "0 !important",
                            borderBottomRightRadius: "0 !important",
                        },
                        ...wrapperSx, // 사용자 정의 스타일 (padding 등)
                    }}
                >
                    <Box
                        sx={{
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                            // header: 상단 고정, 높이 자동
                            "& [data-role='header']": {
                                flexShrink: 0,
                            },
                            // content: 중앙 스크롤 영역, 남은 공간 차지
                            "& [data-role='content']": {
                                flex: 1,
                                overflowY: "auto",
                                overflowX: "hidden",
                                minHeight: 0, // flexbox 스크롤을 위한 필수 속성
                            },
                            // footer: 하단 고정, 높이 자동
                            "& [data-role='footer']": {
                                flexShrink: 0,
                                borderTop: `1px solid ${theme.palette.divider}`,
                            },
                        }}
                    >
                        {children}
                    </Box>
                </Paper>
            </ClickAwayListener>
        </Popper>
    );
};

export default AdaptivePopper;
